import { useEffect } from 'react';
import type React from 'react';
import type { TemplateSigningField } from '../types/template';

/**
 * Seeds prefilled author-field values from each field's template-time
 * `defaultValue` into the envelope wizard's `prefilledFieldValues` map.
 *
 * Invariants:
 *   - never overwrite a user edit — skip any key that already has a value
 *   - always seed newly-added fields when `authorFields` grows mid-session
 *   - idempotent under StrictMode — the setState updater is pure
 */
export function useSeedAuthorDefaults(
  authorFields: TemplateSigningField[],
  fieldKey: (f: TemplateSigningField) => string,
  setPrefilledFieldValues: React.Dispatch<React.SetStateAction<Record<string, string>>>,
): void {
  useEffect(() => {
    if (authorFields.length === 0) return;
    setPrefilledFieldValues((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const f of authorFields) {
        const key = fieldKey(f);
        if (next[key] === undefined && (f.defaultValue ?? '') !== '') {
          next[key] = f.defaultValue!;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [authorFields, fieldKey, setPrefilledFieldValues]);
}
